import { rqlBuilder } from '@extrahorizon/javascript-sdk';
import dayjs from 'dayjs';
import { formatBirthday } from 'utils/dates';
import maxBy from 'lodash/maxBy';
import orderBy from 'lodash/orderBy';
import { assertAffectedRecord, sdk } from 'utils/query';
import {
  Indicator,
  ETLPatientProfileMeasurement,
  ETLPatientProfile,
  ETLPatientProfileGroup,
  ETLPatientProfilePrescription,
  PatientMeasurement,
  PatientGroups,
  PatientProfile,
  getPortalPatientId,
} from 'utils/models';
import { PatientLogsVariables, UpdateCustomFieldsVariables } from 'models/profile';
import { ActivePeriod } from 'models/prescription';
import DataLoader from 'dataloader';
import { EmptyParametersError } from './errors/EmptyParametersError';
import { ElementNotFoundError } from './errors/ElementNotFoundError';
import { getPrescriptionActivePeriod } from '../hooks/api/prescription/usePrescriptionActivePeriod';

export const getProfile = async (id?: string) => {
  if (!id) throw new EmptyParametersError();
  const result = await sdk.profiles.findById(id);
  if (!result) throw new ElementNotFoundError();

  return result;
};

export function getProfiles(ids: string[], limit = 20) {
  return sdk.profiles.find({
    rql: rqlBuilder().in('id', ids).limit(limit).build(),
  });
}

export const getIndicator = (color?: string): Indicator => {
  switch (color) {
    case 'green':
      return Indicator.Normal;
    case 'orange':
      return Indicator.Warning;
    case 'red':
      return Indicator.Urgent;
    case 'blue':
      return Indicator.Quality;
    default:
      return Indicator.NoResult;
  }
};

const formatMeasurement = (measurement: ETLPatientProfileMeasurement) => {
  if (!measurement) {
    return undefined;
  }
  const unixTimeStamp = dayjs(measurement.timestamp).valueOf();
  const formattedMeasurement: PatientMeasurement = {
    indicator: getIndicator(measurement.mostSevereLabel.color),
    timestamp: unixTimeStamp,
    diagnosisWeight: measurement.mostSevereLabel.weight,
  };
  return formattedMeasurement;
};

const getPatientGroups = (
  group: ETLPatientProfileGroup,
  prescription: ETLPatientProfilePrescription,
  lastMeasurement: ETLPatientProfileMeasurement,
  lastMostSevereMeasurement: ETLPatientProfileMeasurement,
  activePeriod: ActivePeriod,
): PatientGroups[] => {
  const now = dayjs();

  const isExpired = dayjs(prescription.endTimestamp).isBefore(now);
  const dateDiff = dayjs(prescription.endTimestamp).diff(now, 'day', true);

  return [
    {
      id: group.id,
      expired: isExpired,
      daysLeft: dateDiff > 0 ? dateDiff : 0,
      activePeriod,
      endDate: prescription.endTimestamp,
      lastMeasurement: formatMeasurement(lastMeasurement),
      mostSevereMeasurement: formatMeasurement(lastMostSevereMeasurement),
    },
  ];
};

const sortPatientActivePeriod = async (mergedPatientList: PatientProfile[]) => {
  for (const mergedPatient of mergedPatientList) {
    if (mergedPatient.groups.length > 1) {
      const activePeriod = await getPrescriptionActivePeriod(mergedPatient.id);
      mergedPatient.groups = mergedPatient.groups?.map((group) => {
        const groupClone = { ...group };
        groupClone.activePeriod = activePeriod?.find((period) => group.id === period.groupId);
        return groupClone;
      });
      mergedPatient.groups.sort((a, b) => {
        const startTimestampA = a.activePeriod?.startTimestamp || 0;
        const startTimestampB = b.activePeriod?.startTimestamp || 0;
        return startTimestampB - startTimestampA;
      });
    }
  }
};

export const createAllPatientProfile = async (patientList: ETLPatientProfile[]): Promise<PatientProfile[]> => {
  const patients: PatientProfile[] = patientList.map((patient: ETLPatientProfile) => ({
    id: patient.user.id,
    firstName: patient.user.firstName,
    lastName: patient.user.lastName,
    gender: patient.user.gender,
    birthday: formatBirthday(patient.user.birthday, true),
    patientId: getPortalPatientId(patient.user.externalPatientIds),
    groups: getPatientGroups(
      patient.group,
      patient.prescription,
      patient.lastMeasurement,
      patient.lastMostSevereMeasurement,
      patient.activePeriod,
    ),
  }));

  const mergedPatientList: PatientProfile[] = patients.reduce(
    (accumulator: PatientProfile[], currentPatient: PatientProfile) => {
      // Check if patient is already in the new list
      const doesCurrentPatientExists: boolean =
        accumulator.find((patient) => patient.id === currentPatient.id) !== undefined;
      if (doesCurrentPatientExists) {
        return accumulator;
      }

      accumulator.push(currentPatient);
      // Find additional rows for the same patient
      const moreCurrentPatientRows: PatientProfile[] = patients.filter((toFind) => currentPatient.id === toFind.id);
      if (moreCurrentPatientRows.length <= 0) {
        return accumulator;
      }

      // Fetch all group of the patients to select active groups when available and last non active if no active groups available
      const allGroups: PatientGroups[] = moreCurrentPatientRows.map((patient) => patient.groups[0]);
      const activeGroups = allGroups.filter((group) => !group.expired);
      let groups: PatientGroups[] = [];
      if (activeGroups.length > 0) {
        groups = activeGroups;
      } else {
        const lastExpiredGroup = maxBy(
          allGroups.filter((group) => group.expired),
          (group) => group.endDate,
        );
        if (lastExpiredGroup) {
          groups = [lastExpiredGroup];
        }
      }
      // filter out groups with no last measurement
      const groupsWithLastMeasurement: PatientGroups[] = allGroups.filter(
        (group) => group.lastMeasurement !== undefined,
      );

      if (groupsWithLastMeasurement.length <= 0) {
        accumulator[accumulator.indexOf(currentPatient)] = { ...currentPatient, groups }; // update patient only with groups
        return accumulator;
      }

      // get last and most severe measurement for the patient
      const lastMeasurement = maxBy(
        groupsWithLastMeasurement,
        (group) => group.lastMeasurement?.timestamp,
      )?.lastMeasurement;
      const mostSevereMeasurement = orderBy(
        groupsWithLastMeasurement,
        ['mostSevereMeasurement.diagnosisWeight', 'mostSevereMeasurement.timestamp'],
        ['desc', 'desc'],
      ).find(Boolean)?.mostSevereMeasurement;

      // update patient with new values
      accumulator[accumulator.indexOf(currentPatient)] = {
        ...currentPatient,
        mostSevereMeasurement,
        lastMeasurement,
        groups,
      };
      return accumulator;
    },
    [],
  );

  await sortPatientActivePeriod(mergedPatientList);

  return mergedPatientList;
};

export const getPatientLogsByGroup = async ({ groupId, profileId }: PatientLogsVariables) => {
  if (!profileId || !groupId) throw new EmptyParametersError();
  const rql = rqlBuilder().sort('-update_timestamp').build();
  return await sdk.profiles.logs.findAll(profileId, groupId, { rql });
};

interface ModifyGroupLoggingVariables {
  profileId?: string;
  groupId?: string;
  entryId?: string;
  text?: string;
}

export const addGroupLogging = async ({ profileId, groupId, text }: Omit<ModifyGroupLoggingVariables, 'entryId'>) => {
  if (!profileId || !groupId || !text) throw new EmptyParametersError();
  await sdk.profiles.logs.create(profileId, groupId, { text });
};

export const updateGroupLogging = async ({ profileId, groupId, text, entryId }: ModifyGroupLoggingVariables) => {
  if (!profileId || !groupId || !entryId || !text) throw new EmptyParametersError();

  await sdk.profiles.logs.update(profileId, groupId, entryId, { text });
};

export const deleteGroupLogging = async ({
  profileId,
  groupId,
  entryId,
}: Omit<ModifyGroupLoggingVariables, 'text'>) => {
  if (!profileId || !groupId || !entryId || !entryId) throw new EmptyParametersError();
  const result = await sdk.profiles.logs.remove(profileId, groupId, entryId);
  return assertAffectedRecord({ result });
};

export const updateCustomField = async (variables: UpdateCustomFieldsVariables) => {
  const { profileId, customField, value } = variables;
  if (!profileId || !customField) throw new EmptyParametersError();

  const rql = rqlBuilder().eq('id', profileId).build();
  const result = await sdk.profiles.update(rql, {
    customFields: { [customField]: value },
  });

  return assertAffectedRecord({ result });
};

export const profileLoader = new DataLoader(
  async (ids: readonly string[]) => {
    const profiles = await getProfiles([...ids], 20);
    const profileMap = new Map(profiles.data.map((p) => [p.id, p]));
    return ids.map((id) => profileMap.get(id) || null);
  },
  {
    // Cache using React Query
    cache: false,
    maxBatchSize: 20,
  },
);
